import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { IChoosenFilterEntity, IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { CertificateService } from 'app/library/certificate/services/certificate.service';
import {
    DeselectSelectedFilteredParticipants,
    NotifySelectedProgramExecutionCourseOfferingParticipants,
    NotifySelectedProgramExecutionCourseOfferingParticipantsSuccess,
    SelectFilteredParticipants,
    SortCourseOfferingParticipants,
    ToggleSelectedParticipants,
    UnsetCourseOfferingParticipants,
} from 'app/library/course-offering-participants/actions';
import { CourseOfferingParticipantsState } from 'app/library/course-offering-participants/models/course-offering-participants.state';
import { CourseOfferingParticipantsService } from 'app/library/course-offering-participants/services/course-offering-participants.service';
import { CourseOffering } from 'app/library/course-offering/models/course-offering.model';
import { CourseOfferingService } from 'app/library/course-offering/services/course-offering.service';
import { ExtendedAttributeCategoryEnum } from 'app/library/extended-attributes/enums/extended-attribute-category.enum';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeService } from 'app/library/extended-attributes/services/extended-attribute.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { ModuleCompletitionStatuses } from 'app/library/module-user/models/module-completition-status';
import { ModuleUserBookingRequestType } from 'app/library/module-user/enums/module-user-booking-request-type.enum';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { getModuleUserDateRangeFiltersFromChosenFilters } from 'app/library/module-user/utils/module-user-date-range-filters.util';
import { ModuleUserState } from 'app/library/module-user/models/module-user.state';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { ScheduledJob } from 'app/library/scheduler/models/scheduled-job.model';
import { SchedulerService } from 'app/library/scheduler/services/scheduler-service';
import { ManageUserCertificatesDialogComponent } from 'app/main/pages/dialogs/manage-user-certificates-dialog/manage-user-certificates-dialog.component';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { ScheduleSubjectTextDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/schedule-subject-text/schedule-subject-text.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { ScheduleDialogType } from 'app/projects/shared/src/lib/enums/schedule-dialog-type.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { UsersState } from 'app/projects/user/src/lib/models/users.state';
import { TinymceService } from 'app/services/tinymce.service';
import { Observable, combineLatest } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CourseOfferingParticipantsDataSource } from './datasource';

@Component({
    selector: 'app-course-offering-participant-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CourseOfferingParticipantListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @Input()
    module$: Observable<IChoosenFilterEntity[]>;
    moduleIds: string[] = [];

    @Input()
    certificates$: Observable<IChoosenFilterEntity[]>;
    certificateIds: string[] = [];

    @Input()
    completions$: Observable<IChoosenFilterEntity[]>;
    completionsIds: string[] = [];

    @Input()
    isUnassignedSelectedCertificates$: Observable<boolean>;
    isUnassignedSelectedCertificates: boolean;

    @Input()
    audiences$: Observable<IChoosenFilterEntity[]>;
    audienceIds: string[] = [];

    @Input()
    teams$: Observable<IChoosenFilterEntity[]>;
    teamIds: string[] = [];
    excludedTeamIds: string[] = [];

    @Input()
    teamRoleIds$: Observable<string[]>;
    teamRoleIds: string[] = [];

    @Input()
    extendedAttributes$: Observable<IChoosenFilterInterface[]>;
    extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [];

    @Input()
    isUnassignedSelectedAudiences$: Observable<boolean>;
    isUnassignedSelectedAudiences: boolean;

    @Input()
    isUnassignedSelectedTeams$: Observable<boolean>;
    isUnassignedSelectedTeams: boolean;

    @Input()
    loadUser$: Observable<boolean>;

    @Input()
    courseOffering: CourseOffering;

    @Input()
    programExecutionId: string;

    @Input()
    isProgramExecutionListed: boolean = null;

    @Input()
    totalCount$: CourseOffering;

    @Input()
    searchInput: FormControl;

    @Input()
    displayedColumns: string[];

    @Output()
    closeDialog = new EventEmitter();

    @Select(CourseOfferingParticipantsState.isFetchingList)
    isFetching$: Observable<boolean>;

    @Select(CourseOfferingParticipantsState.getEntities)
    participant$: Observable<ModuleUser[]>;

    @Select(CourseOfferingParticipantsState.getSelectedEntityIds)
    selectedParticipantId$: Observable<string[]>;
    selectedParticipantIds: string[] = [];

    @Select(CourseOfferingParticipantsState.getFiltersList)
    selectedFilteredList$: Observable<string[]>;

    @Select(UserState.getEntities)
    user$: Observable<User[]>;
    users: User[];

    @Select(AllUsersState.getEntities)
    allUser$: Observable<User[]>;

    @Select(UsersState.getFilteredEntities)
    filteredUser$: Observable<User[]>;

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(ModuleUserState.getEntities)
    moduleUser$: Observable<ModuleUser[]>;
    moduleUsers: ModuleUser[];

    @Select(MyModulesState.getEntities)
    myModuleUser$: Observable<ModuleUser[]>;
    myModuleUsers: ModuleUser[];
    moduleUser: ModuleUser;

    get showEditOptions(): boolean {
        let toReturn = false;

        if (this.profile) {
            toReturn = toReturn || this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        }

        if (this.moduleUser) {
            toReturn = toReturn || this.moduleUser.isManager;
        }

        return toReturn;
    }

    profileId = this._store.selectSnapshot(UserState).me;

    isCertificateOfAttendanceFeatureEnabled: boolean;
    isCourseCatalogFeatureEnabled: boolean;
    isUserDetailsCardHidden = false;

    isActionBoxHovered: boolean = false;

    @HostListener('mouseenter', ['$event'])
    onMouseEnter(e: Event) {
        if (e) {
            this.isActionBoxHovered = true;
        }
    }

    @HostListener('mouseleave', ['$event'])
    onMouseLeave(e: Event) {
        if (e) {
            this.isActionBoxHovered = false;
        }
    }

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    @ViewChild(MatSort)
    sort: MatSort;

    @ViewChild('columnsSelect')
    columnsSelect: MatSelect;

    possibleTableColumns: { id: string; translation: string }[] = [];
    extendedAttributes: ExtendedAttribute[] = [];
    tableColumnsDropdown = new FormControl(null);
    isExtendedAttributesFeatureEnabled: boolean;

    private _baseColumns: string[] = [];
    private _columnsSnapshot: string[] | null = null;
    private _applyClicked = false;
    private _displayedColumnsStorageKey = 'courseOfferingParticipantsDisplayedColumns';

    dataSource: CourseOfferingParticipantsDataSource;
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    manageCertificateDialogRef: MatDialogRef<ManageUserCertificatesDialogComponent>;
    moduleKind = ModuleKind;
    moduleStatuses = ModuleStatuses;

    moduleCompletitionStatus = ModuleCompletitionStatuses;
    jwtoken: string;
    hideEmail = false;

    sortBy = this._store.selectSnapshot(CourseOfferingParticipantsState.getSortBy);
    sortOrder = this._store.selectSnapshot(CourseOfferingParticipantsState.getSortOrder);

    constructor(
        private _store: Store,
        private _matDialog: MatDialog,
        private _featureService: FeatureService,
        private _action$: Actions,
        private _tinymceService: TinymceService,
        private _courseOfferingParticipantsService: CourseOfferingParticipantsService,
        private _certificateService: CertificateService,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _schedulerService: SchedulerService,
        private _moduleManagersService: ModuleManagersService,
        private _moduleGlobalService: ModuleGlobalService,
        private _tenantDataService: TenantDataService,
        private _courseOfferingService: CourseOfferingService,
        private _extendedAttributeService: ExtendedAttributeService,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {
        super();
        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateOfAttendance);
        this.isCourseCatalogFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CourseCatalog);
        this.isExtendedAttributesFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ExtendedAttributes);
    }

    async ngOnInit(): Promise<void> {
        await this._setupTableColumns();

        this.loadUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadUsersPage(resetPageIndex);
            });

        this.profile$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((profile) => !!profile)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });

        this.myModuleUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((myModuleUsers) => !!myModuleUsers)
            )
            .subscribe((myModuleUsers) => {
                this.myModuleUsers = myModuleUsers;

                if (this.courseOffering.id) {
                    this.moduleUser = myModuleUsers.filter((tempModuleUser) => tempModuleUser.moduleId === this.courseOffering.id)[0];
                }
            });

        this.user$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((users) => !!users)
            )
            .subscribe((users) => {
                this.checkboxes = {};

                users.map((user) => {
                    this.checkboxes[user.id] = this.selectedParticipantIds.includes(user.id);
                });
            });

        this.moduleUser$
            .pipe(
                filter((moduleUsers) => !!moduleUsers),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((moduleUsers) => {
                this.moduleUsers = moduleUsers;
            });

        this.selectedParticipantId$
            .pipe(
                filter((selectedParticipantIds) => !!selectedParticipantIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedParticipantIds) => {
                for (const id in this.checkboxes) {
                    if (!this.checkboxes.hasOwnProperty(id)) {
                        continue;
                    }

                    this.checkboxes[id] = selectedParticipantIds.includes(id);
                }

                this.selectedParticipantIds = selectedParticipantIds;
            });

        this.participant$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((bookings) => !!bookings)
            )
            .subscribe((bookings) => {
                this.checkboxes = {};

                bookings.map((booking) => {
                    this.checkboxes[booking.id] = this.selectedParticipantIds.includes(booking.id);
                });
            });

        this.isUnassignedSelectedCertificates$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.isUnassignedSelectedCertificates = value;
        });

        if (this.isUnassignedSelectedAudiences$) {
            this.isUnassignedSelectedAudiences$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
                this.isUnassignedSelectedAudiences = value;
            });
        }

        if (this.isUnassignedSelectedTeams$) {
            this.isUnassignedSelectedTeams$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
                this.isUnassignedSelectedTeams = value;
            });
        }

        this.dataSource = new CourseOfferingParticipantsDataSource(this.participant$, this._action$, this._courseOfferingParticipantsService);

        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortCourseOfferingParticipants(sort));

                this.loadUsersPage(true);
            });

            this.paginator.pageSize = this._pageSizePreferenceService.getPageSize('course-offering-participants.list', [5, 10, 25, 50], 10);

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            this.loadUsersPage(false);
        });
    }

    ngAfterViewInit(): void {
        combineLatest([this.module$, this.certificates$, this.completions$, this.audiences$, this.teams$, this.extendedAttributes$, this.teamRoleIds$])
            .pipe(
                filter(([selectedModules, selectedCertificates, selectedCompletions, selectedAudiences, selectedTeams, extendedAttributes, selectedTeamRoleIds], index) => {
                    this.moduleIds = [];
                    if (selectedModules?.length) {
                        selectedModules.forEach((selectedModule) => {
                            this.moduleIds.push(selectedModule.id);
                        });
                    }

                    this.certificateIds = [];
                    if (selectedCertificates?.length) {
                        selectedCertificates.forEach((selectedCertificate) => {
                            this.certificateIds.push(selectedCertificate.id);
                        });
                    }

                    this.completionsIds = [];
                    if (selectedCompletions?.length) {
                        selectedCompletions.forEach((selectedCompletion) => {
                            this.completionsIds.push(selectedCompletion.id);
                        });
                    }

                    this.audienceIds = [];
                    if (selectedAudiences?.length) {
                        selectedAudiences.forEach((selectedAudience) => {
                            this.audienceIds.push(selectedAudience.id);
                        });
                    }

                    this.teamIds = [];
                    this.excludedTeamIds = [];
                    if (selectedTeams?.length) {
                        selectedTeams.forEach((selectedTeam) => {
                            this.teamIds.push(selectedTeam.id);
                            this.excludedTeamIds = [...this.excludedTeamIds, ...(selectedTeam.excludedTeamIds || [])];
                        });
                        this.excludedTeamIds = Array.from(new Set(this.excludedTeamIds));
                    }

                    this.extendedAttributesFilters = [];
                    if (extendedAttributes?.length) {
                        this.extendedAttributesFilters = extendedAttributes;
                    }

                    this.teamRoleIds = selectedTeamRoleIds?.length ? selectedTeamRoleIds : [];

                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.loadUsersPage(true);
                }, 200);
            });
    }

    onColumnsDropdownOpenedChange(opened: boolean): void {
        if (opened) {
            this._columnsSnapshot = [...this.displayedColumns];
        } else {
            if (this._applyClicked) {
                this._applyClicked = false;
            } else if (this._columnsSnapshot) {
                this.tableColumnsDropdown.setValue([...this._columnsSnapshot], { emitEvent: false });
            }
            this._columnsSnapshot = null;
        }
    }

    applyColumns(): void {
        this._applyClicked = true;
        const currentValue = this.tableColumnsDropdown.value as string[];

        if (currentValue) {
            this.displayedColumns = [...currentValue];
            this._saveColumnsToSessionStorage();
            this.loadUsersPage();
        }

        this.columnsSelect.close();
    }

    private async _setupTableColumns(): Promise<void> {
        this._baseColumns = this.displayedColumns ? [...this.displayedColumns] : [];
        this._buildPossibleTableColumns();

        if (this.isExtendedAttributesFeatureEnabled) {
            await this._extendedAttributeService.fetchExtendedAttributes([ExtendedAttributeCategoryEnum.USER]);
            this.extendedAttributes = this._extendedAttributeService.getExtendedAttributes();

            this.extendedAttributes.forEach((attribute) => {
                const insertIndex = this.possibleTableColumns.findIndex((column) => column.id === 'buttons');
                const column = { id: attribute.name, translation: attribute.translationKey };

                if (insertIndex >= 0) {
                    this.possibleTableColumns.splice(insertIndex, 0, column);
                } else {
                    this.possibleTableColumns.push(column);
                }
            });
        }

        this._setSelectedColumns();
    }

    private _buildPossibleTableColumns(): void {
        const isTemplate = this.courseOffering?.courseTemplate?.kind === ModuleKind.Template;
        const translationMap: { [id: string]: string } = {
            checkbox: '',
            avatar: '',
            firstName: 'GENERAL.PROPERTY.FIRST_NAME',
            lastName: 'GENERAL.PROPERTY.LAST_NAME',
            email: 'GENERAL.PROPERTY.EMAIL',
            execution: isTemplate ? 'COURSES.LABEL.COURSES_EXECUTIONS' : 'MODULE.KIND.PROGRAM_EXECUTION.PLURAL',
            completion: isTemplate ? 'GENERAL.PROPERTY.STATUS' : 'PROGRAMS.PROGRAM_COMPLETION',
            certificate: 'GENERAL.PROPERTY.GIVEN_CERTIFICATE',
            buttons: '',
        };

        this.possibleTableColumns = this._baseColumns.map((id) => ({ id, translation: translationMap[id] || '' }));
    }

    private _setSelectedColumns(): void {
        const storedColumns: string[] = JSON.parse(sessionStorage.getItem(this._displayedColumnsStorageKey));
        const selectedColumns = storedColumns?.length ? storedColumns : [...this._baseColumns];

        this.displayedColumns = selectedColumns.filter((columnId) => this.possibleTableColumns.some((column) => column.id === columnId));
        this.tableColumnsDropdown.setValue(this.displayedColumns);
    }

    private _saveColumnsToSessionStorage(): void {
        sessionStorage.setItem(this._displayedColumnsStorageKey, JSON.stringify(this.displayedColumns));
    }

    downloadCertificate(certificate: Certificate): void {
        this._certificateService.downloadFinalGradeCertificate(certificate).then(() => {});
    }

    async unbookParticipant(moduleUserExtended: ModuleUserExtended): Promise<void> {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'PAGE.DIALOG.EDIT_RELEVANT_EXECUTIONS.LABEL.REMOVE_FROM_EXECUTION';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('PAGE.DIALOG.REMOVE_FROM_EXECUTION.LABEL.REMOVE_EXECUTIONS', {
            participantName: moduleUserExtended.user.fullName,
            executionName: moduleUserExtended.module.name,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE_PARTICIPANT';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const payload = {
                    bookings: [{ user_id: moduleUserExtended.userId, execution_id: moduleUserExtended.moduleId }],
                    removeFromProgram: false,
                    programExecutionId: this.programExecutionId,
                };

                await this._courseOfferingParticipantsService.unbookParticipant(payload);
                this.loadUsersPage(true);
            }

            this.confirmDialogRef = null;
        });
    }

    openListingBookings(participant: ModuleUserExtended): void {
        const listingId = participant?.module?.listingId;

        if (!listingId || !this.courseOffering?.id) {
            return;
        }

        this._store.dispatch(new Navigate([`/admin/offering-details/${this.courseOffering.id}/catalog/listing/${listingId}/bookings`]));
        this.closeDialog.emit();
    }

    shouldRemoveBookingViaListing(participant: ModuleUserExtended): boolean {
        if (!this.programExecutionId || !this.isCourseCatalogFeatureEnabled) {
            return false;
        }

        // Listed program: only standalone listing bookings (not linked to program booking).
        if (this.isProgramExecutionListed === true) {
            return participant?.bookingRequestType === ModuleUserBookingRequestType.Active;
        }

        // Unlisted program: any active booking must be cancelled via the listing.
        if (this.isProgramExecutionListed === false) {
            return participant?.bookingRequestType !== ModuleUserBookingRequestType.Inactive;
        }

        // Listed status still loading — prefer booking_request_type when present.
        if (participant?.bookingRequestType === ModuleUserBookingRequestType.Active) {
            return true;
        }

        if (participant?.bookingRequestType === ModuleUserBookingRequestType.ActiveLinkedToProgram) {
            return false;
        }

        return participant?.bookingRequestType !== ModuleUserBookingRequestType.Inactive;
    }

    shouldRemoveFromExecution(participant: ModuleUserExtended): boolean {
        if (!this.programExecutionId) {
            return false;
        }

        // Unlisted program: removal is only via listing, never direct unbook from execution.
        if (this.isProgramExecutionListed === false) {
            return false;
        }

        // Listed program: only bookings linked to the program booking.
        if (this.isProgramExecutionListed === true) {
            return participant?.bookingRequestType === ModuleUserBookingRequestType.ActiveLinkedToProgram;
        }

        if (this.isCourseCatalogFeatureEnabled && participant?.bookingRequestType) {
            return participant.bookingRequestType === ModuleUserBookingRequestType.ActiveLinkedToProgram;
        }

        return !participant?.module?.listingId;
    }

    removeBookingViaListing(participant: ModuleUserExtended): void {
        this.openListingBookings(participant);
    }

    goToModuleParticipantPage(userId: string, module: Module) {
        this.isUserDetailsCardHidden = true;

        if (module.kind === ModuleKind.ProgramExecution) {
            this._store.dispatch(new Navigate([`/admin/offering-details/${this.courseOffering.id}/participants/${userId}/program/${module.id}/overview`]));
        } else {
            this._store.dispatch(new Navigate([`/admin/offering-details/${this.courseOffering.id}/participants/${userId}/course/${module.id}/overview`]));
        }

        this.closeDialog.emit();
    }

    goToModule(module: Module): void {
        const normalizedStatus = (module?.status || '').toLowerCase();
        const courseOfferingId = this.courseOffering?.id || module?.courseOffering?.id;

        if (normalizedStatus === this.moduleStatuses.PLANNED && courseOfferingId) {
            this._store.dispatch(new Navigate([`/admin/offering-details/${courseOfferingId}/catalog/execution/${module.id}`]));
            this.closeDialog.emit();
            return;
        }

        this._moduleGlobalService.setParentUrl(window.location.pathname);
        this._moduleManagersService.goToModule(this.profile.id, module, 'overview');
        this.closeDialog.emit();
    }

    async openNotifyDialog(participantId: string): Promise<void> {
        const dialogRef = this._matDialog.open(ScheduleSubjectTextDialogComponent, {
            width: '800px',
            disableClose: true,
        });

        const init: any = this._tinymceService.getInitConfiguration();
        const minNumberOfRows = 6;
        init.min_height = minNumberOfRows * 32 + 20;
        init.content_style = 'img { max-width: 100%; height: auto; }';

        dialogRef.componentInstance.init = init;
        dialogRef.componentInstance.dialogTitle = 'GENERAL.BUTTON.NOTIFY_SELECTED';
        dialogRef.componentInstance.saveButtonText = 'GENERAL.BUTTON.SEND';
        dialogRef.componentInstance.actionNowText = 'GENERAL.BUTTON.SEND';
        dialogRef.componentInstance.subject = '';
        dialogRef.componentInstance.message = this._tenantDataService.tenantConfig?.module_notification?.default_text;
        dialogRef.componentInstance.dialogType = ScheduleDialogType.NotifySelected;
        dialogRef.componentInstance.scheduledJob = null;

        dialogRef.componentInstance.scheduleRemoved.pipe(takeUntil(this._unsubscribeAll)).subscribe((payload: { scheduledJob: ScheduledJob; showSnackBar: boolean }) => {
            if (payload.scheduledJob) {
                this._schedulerService.deleteScheduledJob(payload.scheduledJob, dialogRef, this.courseOffering, payload.showSnackBar);
            }
        });

        dialogRef.componentInstance.scheduleSubjectTextSaved.pipe(takeUntil(this._unsubscribeAll)).subscribe((data) => {
            const successMessage = this._translateService.instant('LIST.MODULE_PARTICIPANTS.MESSAGE.SELECTED_MEMBERS_NOTIFIED', {
                userCount: 1,
            });

            this._store.dispatch(
                new NotifySelectedProgramExecutionCourseOfferingParticipants({
                    programExecutionId: this.programExecutionId,
                    courseOfferingId: this.courseOffering.id,
                    notifySelected: {
                        subject: data.subject,
                        message: data.message,
                        user_ids: Array(participantId).map((id) => parseInt(id)),
                        is_urgent: data.isUrgent,
                    },
                })
            );

            this._action$.pipe(ofActionDispatched(NotifySelectedProgramExecutionCourseOfferingParticipantsSuccess), take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });
            });

            dialogRef.close();
        });
    }

    isAllSelected(): boolean {
        if (this.paginator) {
            return this.paginator.pageIndex < this.paginator.getNumberOfPages() - 1
                ? this.selectedParticipantIds.length === this.paginator.pageSize
                : this.selectedParticipantIds.length === this.paginator.length - this.paginator.pageIndex * this.paginator.pageSize;
        }
    }

    masterToggle(): void {
        this._store.dispatch(this.isAllSelected() ? new DeselectSelectedFilteredParticipants() : new SelectFilteredParticipants());
    }

    onSelectedChange(participantId: string): void {
        this._store.dispatch(new ToggleSelectedParticipants(participantId));
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (this.dataSource && this.paginator) {
            if (resetPageIndex) {
                this.paginator.pageIndex = 0;
            }

            setTimeout(() => {
                this.dataSource.loadCourseOfferingParticipants(
                    this.courseOffering.id,
                    this.programExecutionId,
                    this.searchInput.value,
                    this.sort.active,
                    this.sort.direction,
                    this.paginator.pageSize,
                    this.paginator.pageIndex + 1,
                    this.isUnassignedSelectedCertificates,
                    this.certificateIds,
                    this.completionsIds,
                    this.moduleIds,
                    getModuleUserDateRangeFiltersFromChosenFilters(this._courseOfferingService.getFiltersFromStorage() || []),
                    this.audienceIds,
                    this.teamIds,
                    this.extendedAttributesFilters,
                    this.isUnassignedSelectedAudiences,
                    this.isUnassignedSelectedTeams,
                    this.excludedTeamIds,
                    this.teamIds.length ? this.teamRoleIds : [],
                    this.displayedColumns
                );
            });
        }
    }

    ngOnDestroy(): void {
        this._store.dispatch(new UnsetCourseOfferingParticipants());
    }
}
